/**
 * Electricity: breadth-first frontier propagation over conductive cells.
 *
 * One ply per tick, hard-capped frontier. That cap is the whole reason this is
 * safe — an unbounded flood fill through a flooded room is exactly the kind of
 * chain reaction that turns a 2 ms system into a 200 ms spike.
 *
 * The signature interaction — water conducts electricity — is expressed once:
 * a cell with `Flag.WET` conducts regardless of what it is made of. That single
 * rule covers wet stone, wet enemies, wet floors, brine, and every future
 * material, without any of them knowing about electricity.
 */

import { profiler } from '../core/profiler';
import type { Rng } from '../core/rng';
import { Flag, M, VOID } from './materials';
import { CellWorld } from './world';

const MAX_FRONTIER = 4096;

/** Below this potential the arc dies. */
const MIN_POTENTIAL = 0.06;

/** Wet cells conduct at least this well no matter what they're made of. */
const WET_CONDUCTIVITY = 0.55;

/**
 * Per-ply attenuation through a wet cell — water's own value.
 *
 * Overriding conductivity alone is not enough: that only governs whether charge
 * *enters* a cell. Without also overriding resistance, an arc crossing wet
 * granite still attenuates at granite's 0.5 per ply and dies after three cells,
 * which defeats the entire point of the rule. A film of water makes a cell
 * behave electrically like water in both directions.
 */
const WET_RESISTANCE = 0.05;

/**
 * Pack a signed cell coordinate pair into one non-negative key.
 *
 * `((x + 32768) << 16) | (y + 32768)` looks like it works and does not: for any
 * `x >= 0` the biased value is at least 0x8000, so `<< 16` lands in the int32
 * sign bit and the key goes negative. The decode then used an *arithmetic*
 * `>> 16` and recovered `x - 65536` — so every `CHARGED` clear was aimed 65536
 * cells to the left, at chunks that do not exist, and the flag was never
 * actually cleared. Verified: a 30-cell iron bar stayed `CHARGED` forever,
 * strobing cyan in the terrain shader at 60 Hz and re-rolling every
 * `requiresFlagA: CHARGED` reaction against those cells on every subsequent
 * tick, for the life of the session.
 *
 * The fix is a *logical* `>>> 16` on decode. Deliberately not `>>> 0` on
 * encode: that would also work, but it pushes half the keys above 2^31, out of
 * V8's small-integer range and onto the heap — an allocation per `Set` insert,
 * up to 4096 of them a tick, to fix an allocation-free bug. The key stays a
 * signed int32; only the decode changes. Both directions must go through these
 * three functions and nothing else.
 */
function cellKey(x: number, y: number): number {
  return ((x + 32768) << 16) | ((y + 32768) & 0xffff);
}

function keyX(key: number): number {
  return (key >>> 16) - 32768;
}

function keyY(key: number): number {
  return (key & 0xffff) - 32768;
}

export class Charge {
  private world: CellWorld;
  private rng: Rng;

  /** Ping-pong frontier buffers: x, y, potential interleaved. */
  private frontA = new Float32Array(MAX_FRONTIER * 3);
  private frontB = new Float32Array(MAX_FRONTIER * 3);
  private countA = 0;
  private countB = 0;

  /**
   * Cells energised this tick.
   *
   * A `Set` of packed keys, and deliberately still a `Set`: membership is
   * queried once per frontier candidate (four per energised cell) and the
   * alternative — a per-cell visited *flag* in the chunk — would need its own
   * clearing pass. What is not acceptable is re-allocating it: `clear()` on a
   * `Set` keeps the backing store, and the up-to-16k inserts per tick are
   * bounded by `MAX_FRONTIER` rather than by the size of the world.
   */
  private visited = new Set<number>();

  arcsThisTick = 0;

  /**
   * Work units spent this tick: one per frontier cell examined, one per
   * neighbour probed, one per CHARGED flag cleared.
   *
   * `MAX_FRONTIER` was always the quota — it is what makes an unbounded flood
   * fill through a flooded room safe — but nothing counted against it, so the
   * per-system budget fixture had to assert milliseconds to say anything at all
   * about this system. Counting the probes rather than only the arcs matters:
   * `spreadTo` runs four times per energised cell and does the material and flag
   * lookups, so an arc that dies on entry still costs, and `arcsThisTick` alone
   * would report a quiet tick.
   */
  workThisTick = 0;

  /**
   * Ceiling on `workThisTick`, derived from `MAX_FRONTIER` rather than tuned:
   * every frontier cell is examined once (1), probes its four neighbours (4),
   * and has its CHARGED flag cleared by `decay` (1).
   */
  readonly maxWorkPerTick = MAX_FRONTIER * 6;

  /**
   * Emitted for each energised cell so combat can damage entities standing in
   * it and presentation can draw the arc.
   */
  onArc: ((x: number, y: number, potential: number) => void) | null = null;

  constructor(world: CellWorld, rng: Rng) {
    this.world = world;
    this.rng = rng;
  }

  /** Seed a discharge. `potential` 1.0 is a full lightning strike. */
  inject(x: number, y: number, potential = 1): void {
    if (this.countA >= MAX_FRONTIER) return;
    const o = this.countA * 3;
    this.frontA[o] = x;
    this.frontA[o + 1] = y;
    this.frontA[o + 2] = potential;
    this.countA++;
  }

  step(): void {
    profiler.begin('charge');
    this.arcsThisTick = 0;
    // Reset here and not in `decay`: the two run as one pipeline stage and the
    // budget fixture reads the pair's combined cost.
    this.workThisTick = 0;

    if (this.countA === 0) {
      profiler.end('charge');
      return;
    }

    const w = this.world;
    this.visited.clear();
    this.countB = 0;

    for (let i = 0; i < this.countA; i++) {
      this.workThisTick++;
      const o = i * 3;
      const x = this.frontA[o] | 0;
      const y = this.frontA[o + 1] | 0;
      const p = this.frontA[o + 2];

      const key = cellKey(x, y);
      if (this.visited.has(key)) continue;
      this.visited.add(key);

      const mat = w.peek(x, y);
      const cellFlags = w.getFlags(x, y);
      if (mat === VOID && !(cellFlags & Flag.WET)) continue;

      w.addFlag(x, y, Flag.CHARGED);
      this.arcsThisTick++;
      if (this.onArc) this.onArc(x, y, p);

      // Conductors that can't take it break. This is what makes circuit plate
      // and rusted machinery a liability rather than free wiring.
      if (M.shortCircuits[mat] === 1 && p > 0.5) {
        const shattered = M.shatteredInto[mat];
        w.transmute(x, y, shattered >= 0 ? shattered : VOID);
        w.addTemp(x, y, 220 * p);
        continue;
      }

      // Attenuate. Mercury has chargeResistance 0 — it superconducts, and an
      // arc crosses a mercury pool at full strength.
      const resist =
        cellFlags & Flag.WET
          ? Math.min(M.chargeResistance[mat], WET_RESISTANCE)
          : M.chargeResistance[mat];
      const next = p * (1 - resist);
      if (next < MIN_POTENTIAL) continue;

      this.spreadTo(x + 1, y, next);
      this.spreadTo(x - 1, y, next);
      this.spreadTo(x, y + 1, next);
      this.spreadTo(x, y - 1, next);
    }

    // Swap frontiers.
    const t = this.frontA;
    this.frontA = this.frontB;
    this.frontB = t;
    this.countA = this.countB;
    this.countB = 0;

    profiler.setCount('arcs', this.arcsThisTick);
    profiler.end('charge');
  }

  private spreadTo(x: number, y: number, potential: number): void {
    this.workThisTick++;
    if (this.countB >= MAX_FRONTIER) return;
    const key = cellKey(x, y);
    if (this.visited.has(key)) return;

    const mat = this.world.peek(x, y);
    const flags = this.world.getFlags(x, y);

    let conductivity = M.electricConductivity[mat];
    if (flags & Flag.WET) conductivity = Math.max(conductivity, WET_CONDUCTIVITY);
    if (conductivity <= 0) return;

    // Conductivity gates *entry*; it deliberately does not scale the potential.
    // Attenuating once on entry and again by resistance on exit compounds to
    // roughly the square of the intended loss, which killed arcs after about
    // five cells and made a flooded room electrically inert — the exact
    // opposite of the behaviour the water/lightning rule exists to produce.
    const o = this.countB * 3;
    this.frontB[o] = x;
    this.frontB[o + 1] = y;
    this.frontB[o + 2] = potential;
    this.countB++;
  }

  /**
   * Clear CHARGED flags set last tick. Called after combat has had a chance to
   * read them, so an entity standing in an arc is damaged exactly once.
   */
  decay(): void {
    for (const key of this.visited) {
      this.workThisTick++;
      this.world.clearFlag(keyX(key), keyY(key), Flag.CHARGED);
    }
  }

  get active(): boolean {
    return this.countA > 0;
  }
}
